import React from "react";
import "../resources/styles/components/verify-code.scss";
import { Link, Redirect, RouteComponentProps } from "react-router-dom";
import * as utils from "../util/utils";
import * as mfaAPI from "../services/mfaAPI";
import CountdownTimer from "./common-components/CountdownTimer";
import { Modal } from "react-bootstrap";
import NotificationModal from "./common-components/NotificationModal";
import { VerifyHeader } from "./common-components/VerifyHeader";
import VerifyResponse from "../classes/VerifyResponse";
import { errorMessages } from "../constants/error-messages";
import VerifyCodeData from "../classes/VerifyCodeData";

interface Props extends RouteComponentProps<any>{}

type State = {
    chosenContactInfo: string,
    activeContactCard: boolean, 
    hasPhoneNumbers: boolean,
    rememberMe: boolean,
    codeTimedOut: boolean,
    showModal: boolean,
    modalText: string,
    modalBodyText: string,
    modalCloseButtonText: string,
    modalRequestNewCode: boolean,
    errorModal: boolean,
    verificationCode: number,
    validationAttempts: number,
    redirect: boolean,
    restartTimerKey: number,
    timeFormat: string,
    resendDisabled: boolean,
    minutes: number,
};

/**
 * Component used to enter the verification code gotten from the chosen contact method.
 */
export default class VerifyCode extends React.Component<Props, State>{
	constructor(props:Props) {
		super(props);

		this.state = {
            chosenContactInfo: 'email',
            activeContactCard: false, 
            hasPhoneNumbers: false,
            rememberMe: true,
            codeTimedOut: false,
            showModal: false,
            modalText: '',
            modalBodyText: '',
            modalCloseButtonText: '',
            modalRequestNewCode: false,
            errorModal: false,
            verificationCode: 0,
            validationAttempts: 0,
            redirect: false,
            restartTimerKey: 0,
            timeFormat: 'minutes',
            resendDisabled: false,
            minutes: 10,
		}

        this.updateTimeFormat = this.updateTimeFormat.bind(this);
	}

    componentDidMount = async() => {
        const routeProps = this.props.location.state as any;

        this.setState({
            chosenContactInfo: routeProps.chosenContactInfo,
            activeContactCard: routeProps.activeContactCard,
            hasPhoneNumbers: routeProps.hasPhoneNumbers,
            resendDisabled: routeProps.resendDisabled,
        })

        if (routeProps.resendDisabled) {
            this.setState({
                modalText: 'Error',
                modalCloseButtonText: 'Close',
                modalBodyText: errorMessages.TOO_MANY_ERROR,
                modalRequestNewCode: false,
                errorModal: true,
            });
            this.open()
            this.getCountdownTime();
        }

        this.getIsAlwaysRequired();
    }

    /**
	 * Control to close a modal
	 */
	async close() {
		if(this.state.modalRequestNewCode) {
             await this.resendCode();  
        } else if (this.state.modalCloseButtonText === 'Home') {
            utils.returnToLogin();
        } else {
            this.setState({ showModal: false });
        }
	}
    exitModal() {
        this.setState({
            showModal: false,
            modalRequestNewCode: false,
        });
    }

	/**
	 * Control to open a modal
	 */
	open() {
		this.setState({ showModal: true });
	}

    toggleRememberMe() {
        this.setState({rememberMe: !this.state.rememberMe});
    }

    /**
     * If timer runs out, automatically open appropriate modal &
     * send new code.
     */
    codeTimeoutHandler = () => {
        this.setState({
            modalText: 'Your code has expired.',
            modalBodyText: 'Please request a new code.',
            modalCloseButtonText: 'Request a New Code',
            modalRequestNewCode: true,
            validationAttempts: 0,
            errorModal: true,
            resendDisabled: false,
        });
        this.open();
    }

    updateTimeFormat = () => {
        this.setState({timeFormat: 'seconds'});
    }

    handleTokenChange = (event: any) => {
        const token = parseInt(event.target.value)
        if (!isNaN(token)) {
            event.target.value = token + '';
            this.setState({verificationCode: token});
        } else {
            event.target.value = '';
        }
    }

    /**
     * Handle send code button click
     */
    submitHandler = async (event: any) => {
        event.preventDefault();
        event.target.verificationCode.value = '';
        const enteredCode = this.state.verificationCode;
        const codeValidated = await this.validateVerificationCode(enteredCode);
        const success = codeValidated.success;

        if (success){
            if (this.state.rememberMe){
                // Replace so that member cannot go back to 2FA app and cause potential issues.
                window.parent.location.replace(utils.getHomePageUrl());
            } else {
                this.setState({
                    redirect: true,
                })
            }
        } else if (codeValidated.errorMessages.length > 0) {
            const hasAuthError = errorMessages.AUTH_ERROR === codeValidated.errorMessages[0];
            this.setState({
                modalText: 'Error',
                modalBodyText: codeValidated.errorMessages[0],
                modalCloseButtonText: hasAuthError? 'Home' : 'Close',
                modalRequestNewCode: false,
                errorModal: true,
            });
            this.open()
        } else {
            this.setState({
                modalText: 'Invalid Code',
                modalBodyText: 'Please check that the entered code is correct.',
                modalCloseButtonText: 'Close',
                modalRequestNewCode: false,
                validationAttempts: this.state.validationAttempts + 1,
                errorModal: true,
            });
            this.open();
        }
    }

	render() {
		return (
            <>
                {this.state.redirect ?
                    <Redirect to={{
                            pathname: '/verified',
                            state: {
                                hasPhoneNumbers: this.state.hasPhoneNumbers,
                            }
                        }}
                    />
                : null}

                <VerifyHeader/>
                <div className="mp-bold-text code-information-text">
                    We sent a security code to {this.state.chosenContactInfo}. Please enter it below.
                </div>
                <div className="diff-method-link">
                    <Link to="/" className={"text-mp-secondary" + (this.state.resendDisabled ? " disabled-link" : "")}>
                        Send code using a different method.
                    </Link>
                </div>
                <form onSubmit={(e) => this.submitHandler(e)}>
                    <input autoFocus className="mx-auto mb-20 form-control verification-code-input" name="verificationCode" autoComplete="off" type="text" maxLength={6} placeholder="______" onChange={this.handleTokenChange}/>
                    <div className="text-centered countdown-timer">
                        <CountdownTimer
                            key={this.state.restartTimerKey}
                            remainingMinutes={this.state.minutes} 
                            totalMinutes={10}
                            size={30}
                            strokeWidth={4}
                            codeTimeoutHandler={() => this.codeTimeoutHandler()}
                            updateTimeFormat={this.updateTimeFormat}
                        />
                        <strong>&nbsp;&nbsp;{this.state.timeFormat} until code expires.</strong> Didn't receive a code? <i className={"text-mp-secondary resend-code-btn" + (this.state.resendDisabled ? " disabled-link" : "")} onClick={() => this.resendCode()}>Resend Code</i>
                    </div>
                    <hr className="bottom-hr"/>
                    <div className="row">
                        <div className="mx-auto mb-20 d-flex remeber-checkbox">
                            <span className={"remember-checkbox-icon " + ((this.state.rememberMe) ? "mp-icon-check-filled" : "mp-icon-check-unfilled")} onClick={() => this.toggleRememberMe()} />
                            <div className="remember-device-text">
                                Remember this device.
                            </div>
                        </div>
                    </div>
                    <div className="text-centered">
                        <i>If you have selected to enter a security code each time you log in, this device will not be remembered.</i>
                    </div>
                    <div className="text-centered">
                        <i>Please update your settings if you want this to be recognized as a trusted device.</i>
                    </div>
                    <div className="text-centered mt-4">
                        <button type="submit" className={"mp-btn mp-btn-secondary mp-btn-large"}>
                            Verify Code
                        </button>
                    </div>
                </form>
                <Modal show={this.state.showModal} onHide={() => this.close()} animation={false} centered>
                    <NotificationModal
                        modalText={this.state.modalText}
                        modalBodyText={this.state.modalBodyText}
                        modalCloseButtonText={this.state.modalCloseButtonText}
                        successNotification={!this.state.errorModal}
                        modalButtonHandler={() => this.close()}
                        exitModalHandler={() => this.exitModal()}
                    />
                </Modal>
            </>
		);
	}

    validateVerificationCode = async (enteredCode: number): Promise<VerifyResponse> => {
        const payload = new VerifyCodeData(enteredCode, this.state.rememberMe);
        return mfaAPI.validateToken(payload);
    }

    resendCode = async (): Promise<void> => {
        let codeSent = await mfaAPI.generateToken(this.state.activeContactCard)
        if (codeSent.success) {
            this.setState({
                restartTimerKey: this.state.restartTimerKey + 1,
                minutes: 10,
                timeFormat: 'minutes',
                modalText: 'Sent!',
                modalBodyText: 'We sent another code to ' + this.state.chosenContactInfo,
                modalCloseButtonText: 'Close',
                modalRequestNewCode: false,
                errorModal: false,
            });
            this.open();
        } else {
            const hasAuthError = errorMessages.AUTH_ERROR === codeSent.errorMessages[0];
            const tooManyCodes = errorMessages.TOO_MANY_ERROR === codeSent.errorMessages[0];
            this.setState({
                modalText: 'Error',
                modalBodyText: codeSent.errorMessages[0],
                modalCloseButtonText: hasAuthError ? 'Home' : 'Close',
                modalRequestNewCode: false,
                errorModal: true,
                resendDisabled: tooManyCodes,
            });
            this.open();
        }
    }

    getCountdownTime = async (): Promise<void> => {
        let time = await mfaAPI.countdownTime();
        if (time.errorMessages?.length === 0) {
            this.setState({
                minutes: time.minutes,
                restartTimerKey: this.state.validationAttempts + 1,
            })
        } 
    }

    getIsAlwaysRequired = async (): Promise<void> => {
        let response = await mfaAPI.isAlwaysRequired();
        if (response.errorMessages?.length === 0) {
            this.setState({
                rememberMe: !response.isAlwaysRequired,
            })
        }
    }

}